import { useMutation, useQuery } from "@apollo/client/react"
import { ArrowLeft, ExternalLink, Mic, PencilLine } from "lucide-react"
import { useCallback, useEffect, useRef } from "react"
import { Link, useBlocker, useNavigate, useParams } from "react-router-dom"
import { gql } from "~/__generated__"
import { PostSchedulePanel } from "~/calendar/post-schedule-panel"
import { dashboardPath, postPath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { PageLayout } from "~/layouts/page-layout"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/graphql-error"
import { ClipsPanel } from "./clips-panel"
import { SAVE } from "./editorial-queries"
import { PostEditor } from "./post-editor"
import { ReactionRecorder } from "./reaction-recorder"
import { SuggestionBrief } from "./suggestion-brief"

const REACTION = gql(/* GraphQL */ `
  query ReactionWorkspace($sourceId: ID!, $postId: ID!, $hasSource: Boolean!, $hasPost: Boolean!) {
    editorialWorkspace {
      source(id: $sourceId) @include(if: $hasSource) {
        ...EditorialSource
      }
      post(id: $postId) @include(if: $hasPost) {
        ...ReactionPost
      }
    }
  }
`)

export function ReactionScreen() {
  const { sourceId, postId } = useParams()
  const navigate = useNavigate()
  const [savePost] = useMutation(SAVE)
  const recordingPostId = useRef<string | null>(null)
  const dirty = useRef(false)
  const recordingPending = useRef(false)
  const savedDestination = useRef<string | null>(null)
  const onDirtyChange = useCallback((value: boolean) => {
    dirty.current = value
  }, [])
  const onRecordingChange = useCallback((value: boolean) => {
    recordingPending.current = value
  }, [])
  const blocker = useBlocker(
    ({ nextLocation }) =>
      (dirty.current || recordingPending.current) &&
      nextLocation.pathname !== savedDestination.current
  )
  useEffect(() => {
    if (blocker.state === "blocked") {
      if (
        window.confirm(
          "Leave this reaction? Unsaved writing, transcript corrections and recordings will be lost. Download any recording you want to keep first."
        )
      )
        blocker.proceed()
      else blocker.reset()
    }
  }, [blocker])
  const heading = useRef<HTMLHeadingElement>(null)
  const { data, loading, error, refetch } = useQuery(REACTION, {
    variables: {
      sourceId: sourceId ?? "",
      postId: postId ?? "",
      hasSource: !!sourceId,
      hasPost: !!postId,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: 2500,
  })
  const post = data?.editorialWorkspace.post
  const source = data?.editorialWorkspace.source ?? post?.sourceItem
  useDocumentTitle(post ? "Review post" : "Record your reaction")
  useEffect(() => {
    savedDestination.current = null
    heading.current?.scrollIntoView({ block: "start" })
    heading.current?.focus({ preventScroll: true })
  }, [sourceId, postId])

  const missing = !loading && !error && ((sourceId && !source) || (postId && !post))
  return (
    <PageLayout title={postId ? "Your post" : "Your reaction"}>
      <div className="mx-auto max-w-6xl space-y-7 pb-12">
        <Link
          to={dashboardPath({})}
          className="inline-flex items-center gap-2 text-sm underline underline-offset-4"
        >
          <ArrowLeft className="size-4" /> Back to inbox
        </Link>
        <header>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Read · reflect · record
          </p>
          <h2
            ref={heading}
            tabIndex={-1}
            className="mt-3 text-3xl font-semibold tracking-tight outline-none"
          >
            {postId ? "Review your reaction" : "What’s your take?"}
          </h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Start with the source. Bring a real example from your work. Talk it through in your own
            words.
          </p>
        </header>
        {error && <GraphqlError error={error} />}
        {loading && !data && <p role="status">Loading your reaction brief…</p>}
        {missing && (
          <p role="alert">
            This source or post is no longer available. Return to the inbox to choose another.
          </p>
        )}
        {!error && !missing && (data || (!sourceId && !postId)) && (
          <>
            <div className="grid items-start gap-6 lg:grid-cols-2">
              <section className="rounded-2xl border bg-card p-6" aria-label="Source brief">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  01 · Read the source
                </p>
                <h3 className="mt-3 text-xl font-semibold leading-snug">
                  {source?.title ?? post?.title ?? "An idea from your own experience"}
                </h3>
                {source ? (
                  <>
                    <p className="mt-3 text-sm text-muted-foreground">
                      Via {source.feedSource?.name ?? "a saved source"}
                    </p>
                    {source.summary && source.summary.trim().toLowerCase() !== "comments" && (
                      <p className="mt-4 whitespace-pre-wrap text-sm leading-7">{source.summary}</p>
                    )}
                    <p className="mt-3 text-xs text-muted-foreground">
                      Read the original for the full context; feed excerpts may be incomplete.
                    </p>
                    <Button asChild className="mt-5">
                      <a href={source.url} target="_blank" rel="noreferrer">
                        Read original source <ExternalLink className="size-4" />
                      </a>
                    </Button>
                  </>
                ) : (
                  <p className="mt-4 text-sm leading-7 text-muted-foreground">
                    Start with a problem you solved, a conversation that stayed with you, or a
                    change you’re seeing in how businesses build products.
                  </p>
                )}
                <div className="mt-6 border-t pt-5">
                  <h4 className="font-semibold">Why this is in your inbox</h4>
                  {source ? (
                    <SuggestionBrief source={source} />
                  ) : (
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      You’re starting with your own idea, so your experience sets the direction.
                    </p>
                  )}
                </div>
              </section>
              <section className="rounded-2xl bg-muted p-6" aria-label="Reaction prompts">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  02 · Find your angle
                </p>
                <h3 className="mt-3 text-xl font-semibold">
                  What would you tell a client about this?
                </h3>
                <ol className="mt-5 list-decimal space-y-4 pl-5 text-sm leading-7">
                  <li>What caught your attention, and what do you agree or disagree with?</li>
                  <li>
                    Where have you seen something like this in your own work? Share one concrete
                    example.
                  </li>
                  <li>What changes for a small-business owner or a team building a product?</li>
                  <li>What’s one useful next step you would recommend?</li>
                </ol>
                <p className="mt-5 text-sm text-muted-foreground">
                  Aim for 3–10 minutes. A specific story is more useful than a polished script.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button
                    onClick={() =>
                      document
                        .getElementById("reaction-recording")
                        ?.scrollIntoView({ behavior: "smooth", block: "start" })
                    }
                  >
                    <Mic className="size-4" /> Record my reaction
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById("post-title")?.focus()}
                  >
                    <PencilLine className="size-4" /> Write instead
                  </Button>
                </div>
              </section>
            </div>
          </>
        )}
        <div hidden={!!error || !!missing || (!data && (!!sourceId || !!postId))}>
          <ReactionRecorder
            postId={post?.id}
            onPendingChange={onRecordingChange}
            sections={post?.recordingSections ?? []}
            editable={post?.editable ?? true}
            onSaved={async () => {
              await refetch()
            }}
            ensurePost={async () => {
              if (postId) return postId
              if (recordingPostId.current) return recordingPostId.current
              if (dirty.current)
                throw new Error(
                  "Save your written draft before recording so those changes are preserved."
                )
              const result = await savePost({
                variables: {
                  input: {
                    title: source?.title.slice(0, 250) ?? "Recorded reaction",
                    body: "",
                    sourceItemId: source?.id,
                  },
                },
              })
              if (result.error) throw result.error
              const id = result.data?.contentPostSave.post.id
              if (!id) throw new Error("Could not prepare your recording. Please retry.")
              recordingPostId.current = id
              savedDestination.current = postPath({ postId: id })
              navigate(savedDestination.current, { replace: true })
              return id
            }}
          />
        </div>
        {!error && !missing && (data || (!sourceId && !postId)) && (
          <PostEditor
            key={post?.id ?? source?.id ?? "idea"}
            post={post ?? undefined}
            source={source}
            onDirtyChange={onDirtyChange}
            onClose={() => navigate(dashboardPath({}))}
            onSaved={async (id) => {
              if (postId === id) await refetch()
              else {
                savedDestination.current = postPath({ postId: id })
                navigate(savedDestination.current, { replace: true })
              }
            }}
          />
        )}
        {post && (
          <ClipsPanel
            postId={post.id}
            hasRecordings={post.recordingSections.some((section) => section.included)}
          />
        )}
        {post && <PostSchedulePanel postId={post.id} />}
      </div>
    </PageLayout>
  )
}
